import { useEffect, useState } from "react";
import Head from "next/head";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styles } from "../../components/installementStyle";
import { styles as form } from "../../components/form";
import { useRouter } from "next/router";
import en from "../../../locales/common/en";
import ar from "../../../locales/common/ar";
import { useSelector } from "react-redux";
import jsonwebtoken from "jsonwebtoken";
import { colors, EnvKey } from "@/utils/const";
import { FetchUserData, Update } from "@/utils/Fetch_userDataFn";
import { useFormik } from "formik";
import { validationSchemaEn } from "@/components/userProfile/en_schema";
import { validationSchemaAr } from "@/components/userProfile/ar_schema";
import { useI18n } from "@/i18n/I18nProvider";

export default function Profile() {
  const router = useRouter();
  const { t, locale } = useI18n();

  const validationSchema =
    locale === "en" ? validationSchemaEn : validationSchemaAr;

  const user = useSelector((state) => state.user);
  const [Data, setData] = useState("");
  const [Loading, SetLoading] = useState(false);
  const [saveLoading, SetSaveLoading] = useState(false);
  const [editable, setEditable] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const initialValues = {
    name: user.name || "",
    email: user.email || "",
    avatar: user.avatar || "",
    refContact: {
      name: user.refContact?.name || "",
      phone: user.refContact?.phone?.startsWith("+2")
        ? user.refContact.phone.slice(2)
        : user.refContact?.phone || "",
    },
    phone: user.phone?.startsWith("+2")
      ? user.phone.slice(2)
      : user.phone || "",
    secondPhone: user.secondPhone?.startsWith("+2")
      ? user.secondPhone.slice(2)
      : user.secondPhone || "",
    nationalId: user.nationalId || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await Update(values, Data, locale, SetSaveLoading);
      setEditable(false);
    },
  });

  useEffect(() => {
    const token =
      typeof window !== "undefined" && localStorage.getItem("UT_UI");

    if (!token) {
      router.push("/login");
      return;
    }

    try {
      const SessionData = jsonwebtoken.verify(token, EnvKey.secretKey);
      const ID = SessionData.id;

      setAuthenticated(true);

      if (!user.name || !user.email || !user.phone || !user.avatar) {
        FetchUserData(ID, SetLoading, setData);
      }
    } catch (error) {
      localStorage.removeItem("UT_UI");
      router.push("/login");
    }
  }, []);
  if (!authenticated) {
    return (
      <div className="box-loading" style={{ minHeight: 600 }}>
        <span className="loader"></span>
      </div>
    );
  }

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        formik.setFieldValue("avatar", e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const displayValue = (val) => (!editable && (!val || val === "") ? "-" : val);

  return (
    <>
      <Head>
        <title>Profile</title>
      </Head>
      {!Loading || Data ? (
        <>
          <Box
            sx={{
              bgcolor: "#F8F8F8",
              direction: locale == "ar" ? "rtl" : "ltr",
            }}
            className="horizontal-safe-padding vertical-safe-padding"
          >
            <Box
              sx={{
                ...styles.box,
                width: { md: "530px" },
                m: "auto",
              }}
            >
              <Typography
                sx={{ ...styles.title, mb: "16px", color: "#535456" }}
              >
                {t.profile.title}
              </Typography>
              <Avatar
                src={formik.values.avatar || Data?.avatar || ""}
                alt="avatar"
                loading="lazy"
                sx={{ width: 160, height: 160, mb: 2 }}
                style={{ borderRadius: "8px 8px 0 0" }}
              />

              <InputLabel htmlFor="email" sx={form.label}>
                {t.profile.email_title}
                {editable && (
                  <Box
                    component="span"
                    style={{
                      fontWeight: 600,
                      padding: "1.5px",
                      marginLeft: t === en ? "2px" : 0,
                      marginRight: t === ar ? "2px" : 0,
                      color: "red",
                    }}
                  >
                    *
                  </Box>
                )}
              </InputLabel>
              <TextField
                fullWidth
                name="email"
                value={displayValue(formik.values.email)}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                }}
              />
              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {" "}
                {formik.touched.email && formik.errors.email}{" "}
              </Box>

              <Divider
                sx={{ my: 2, backgroundColor: "#667085", height: "1px" }}
              />

              <Typography sx={{ ...styles.title, mb: "8px", color: "#535456" }}>
                {t.profile.extraInfo_title}
              </Typography>

              <InputLabel htmlFor="name" sx={form.label}>
                {t.form_labels.full_name}
                {editable && (
                  <Box
                    component="span"
                    style={{
                      fontWeight: 600,
                      // fontSize: "12px",
                      padding: "1.5px",
                      marginLeft: t === en ? "2px" : 0,
                      marginRight: t === ar ? "2px" : 0,
                      color: "red",
                    }}
                  >
                    *
                  </Box>
                )}
              </InputLabel>
              <TextField
                fullWidth
                name="name"
                value={displayValue(formik.values.name)}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                }}
              />

              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {formik.touched.name && formik.errors.name}
              </Box>
              <InputLabel sx={form.label}>
                {t.form_labels.national_id}
                {editable && (
                  <Box
                    component="span"
                    style={{
                      fontWeight: 600,
                      padding: "1.5px",
                      marginLeft: t === en ? "2px" : 0,
                      marginRight: t === ar ? "2px" : 0,
                      color: "red",
                    }}
                  >
                    *
                  </Box>
                )}
              </InputLabel>
              <TextField
                fullWidth
                name="nationalId"
                value={formik.values.nationalId}
                onChange={formik.handleChange}
                InputProps={{ readOnly: true }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                  bgcolor: editable ? "#ffffff" : colors.white_smoke,
                }}
              />

              <InputLabel sx={form.label}>
                {t.form_labels.phone_number}
                {editable && (
                  <Box
                    component="span"
                    style={{
                      fontWeight: 600,
                      padding: "1.5px",
                      marginLeft: t === en ? "2px" : 0,
                      marginRight: t === ar ? "2px" : 0,
                      color: "red",
                    }}
                  >
                    *
                  </Box>
                )}
              </InputLabel>
              <TextField
                fullWidth
                name="phone"
                value={displayValue(
                  formik.values.phone?.startsWith("+2")
                    ? formik.values.phone.slice(2)
                    : formik.values.phone,
                )}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                  direction: locale === "ar" ? "ltr" : "ltr",
                  "& input": {
                    textAlign: locale === "ar" ? "right" : "left",
                  },
                }}
              />
              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {" "}
                {formik.touched.phone && formik.errors.phone}{" "}
              </Box>
              <InputLabel sx={form.label}>
                {t.form_labels.secondPhone}
              </InputLabel>
              <TextField
                fullWidth
                name="secondPhone"
                value={displayValue(formik.values.secondPhone)}
                onChange={formik.handleChange}
                error={
                  formik.touched.secondPhone &&
                  Boolean(formik.errors.secondPhone)
                }
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                  direction: locale === "ar" ? "ltr" : "ltr",
                  "& input": {
                    textAlign: locale === "ar" ? "right" : "left",
                  },
                }}
              />
              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {" "}
                {formik.touched.secondPhone && formik.errors.secondPhone}{" "}
              </Box>
              <InputLabel sx={form.label}>
                {t.form_labels.additionalN}
              </InputLabel>
              <TextField
                fullWidth
                name="refContact.name"
                value={displayValue(formik.values.refContact?.name)}
                onChange={formik.handleChange}
                error={
                  formik.touched.refContact?.name &&
                  Boolean(formik.errors.refContact?.name)
                }
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                }}
              />
              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {" "}
                {formik.touched.refContact?.name &&
                  formik.errors.refContact?.name}{" "}
              </Box>
              <InputLabel sx={form.label}>
                {t.form_labels.additionalP}
              </InputLabel>
              <TextField
                fullWidth
                name="refContact.phone"
                value={displayValue(
                  formik.values.refContact?.phone?.startsWith("+2")
                    ? formik.values.refContact.phone.slice(2)
                    : formik.values.refContact?.phone,
                )}
                onChange={formik.handleChange}
                error={
                  formik.touched.refContact?.phone &&
                  Boolean(formik.errors.refContact?.phone)
                }
                InputProps={{ readOnly: !editable }}
                sx={{
                  ...form.input,
                  mb: 2,
                  color: editable ? "#667085" : "black",
                  direction: locale === "ar" ? "ltr" : "ltr",
                  "& input": {
                    textAlign: locale === "ar" ? "right" : "left",
                  },
                }}
              />
              <Box
                sx={(theme) => ({
                  ...form.helperText(theme),
                  mt: -1,
                  mb: 2,
                })}
              >
                {" "}
                {formik.touched.refContact?.phone &&
                  formik.errors.refContact?.phone}{" "}
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {editable ? (
                  <>
                    <Button
                      onClick={formik.handleSubmit}
                      sx={{
                        ...form.btn,
                        mt: 1,
                      }}
                      style={{
                        width: "50%",
                        backgroundColor: "#2D317F",
                        border: `0px solid #74357D`,
                      }}
                    >
                      {saveLoading ? (
                        <CircularProgress size={20} sx={{ color: "#ffffff" }} />
                      ) : (
                        t.form_labels.save
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditable(false);
                        formik.resetForm();
                      }}
                      sx={{
                        p: {
                          sm: "1.3rem",
                          lg: "1.3rem",
                        }, //
                        borderRadius: "8px",
                        width: "fit-content",
                        height: "3.5rem",
                        maxHeight: "48px",
                        boxShadow: "none",

                        alignSelf: "center",
                        width: "100px",
                        background: "transparent",
                        color: colors.blue,
                        fontWeight: 700,
                        mt: 1,
                        border: "1px solid #D6D9E1",
                        "&:hover": {
                          background: "transparent",
                          color: colors.blue,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {t.form_labels.cancel}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={
                      <Box
                        component="img"
                        src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/edit-table.svg"
                        sx={{
                          ml: 1,
                          textAlign: "center",
                          color: "#fff",
                          width: "20px",
                        }}
                      ></Box>
                    }
                    sx={{
                      p: {
                        sm: "1.3rem",
                        lg: "1.3rem",
                      }, //
                      borderRadius: "8px",
                      width: "fit-content",
                      height: "3.5rem",
                      maxHeight: "48px",
                      boxShadow: "none",

                      alignSelf: "center",
                      width: "100px",
                      background: "transparent",
                      color: "#6F6F6F",
                      fontWeight: 700,
                      // m: 5,
                      mt: 1,
                      border: "1px solid #D6D9E1",
                      "&:hover": {
                        background: "transparent",
                        color: "#6F6F6F",
                        boxShadow: "none",
                      },
                    }}
                    onClick={() => setEditable(true)}
                  >
                    {t.form_labels.edit}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <div className="box-loading">
          <span className="loader"></span>
        </div>
      )}
    </>
  );
}
